import { defineStore } from "pinia";
import { AxiosError } from "axios";
import { axiosInstance } from "@/plugins/https";
import api from "@/_helpers/api";
import { StoreModule } from "@/constants/store.ts";
import { Service } from "@/constants/cloud-apps.ts";
import {
  ComplianceTraining,
  CoursePreview,
  SatConfigurationStatus,
  SatLocale,
  SatServiceProvider,
} from "@/constants/security-awareness.ts";
import {
  normalizeSatSettingsResponse,
  type SatConnection,
  type SatEmailProviderConfig,
  type SatEmailProvidersConfiguration,
  type SatSettings,
} from "@/_store/security-awareness/security-awareness.adapters.ts";
import type { Group, ServiceSelection } from "@/_store/cloud-apps/access-permissions.module.ts";
import { useSnackbarStore } from "@/_store/snackbar.module.ts";
import { i18n } from "@/plugins/i18n.ts";
import type { DeepPartial } from "@/types.ts";

export const MAX_SAT_CONNECTIONS = 3;

export interface SecurityTrainingEmailData {
  emailTitle: string;
  senderName: string;
  senderEmail: string;
  emailBody: string;
}

export interface SendSecurityAwarenessTrainingPayload {
  userEmail: string;
  trainingName: string;
}

export interface SecurityTrainingData {
  startDate: number;
  endDate: number;
  contentName: string;
  contentId: string;
  scheduledSimulationFormat: string;
}

export interface PhishingSimulationSettings {
  phishingSimulationEnabled: boolean;
  phishingAutoEnrolment: boolean;
  scheduledSimulations: SecurityTrainingData[] | null;
  simulationsHistory: SecurityTrainingData[] | null;
  simulationsLoading: boolean;
}

export interface SecurityTrainingSettings {
  trainingEnabled: boolean;
  enableInductionEnrolment: boolean;
  trainingCourses: ComplianceTraining[];
  adaptiveTrainingEnabled: boolean;
}

export interface SecurityAwarenessMetadata {
  daysToAllowList: number;
  bannerDismissed: boolean;
}

export interface SecurityAwarenessState {
  phishingSettings: PhishingSimulationSettings;
  securityTrainingSettings: SecurityTrainingSettings;
  satConnections: SatConnection[];
  preferredLanguageCode: SatLocale;
  randomJourney: boolean;
  showSkeletonLoader: boolean;
  loading: boolean;
  metadata: SecurityAwarenessMetadata;
  coursePreviewRecipients: string[];
  metadataLoading: boolean;
  tenancyId: number; // -1 or >0 if journey exists
}

const defaultSecurityAwarenessState = {
  phishingSettings: {
    phishingSimulationEnabled: false,
    phishingAutoEnrolment: false,
    scheduledSimulations: null,
    simulationsHistory: null,
    simulationsLoading: false,
  },
  securityTrainingSettings: {
    trainingEnabled: false,
    enableInductionEnrolment: false,
    trainingCourses: [],
    adaptiveTrainingEnabled: false,
  },
  preferredLanguageCode: SatLocale.EN_US,
  randomJourney: false,
  tenancyId: -1,
  satConnections: [],
  loading: false,
  showSkeletonLoader: false,
  metadataLoading: false,
  coursePreviewRecipients: [],
  metadata: {
    daysToAllowList: 28,
    bannerDismissed: false,
  },
};

export const useSecurityAwarenessModule = defineStore(StoreModule.SECURITY_AWARENESS, {
  state: (): SecurityAwarenessState => ({ ...defaultSecurityAwarenessState }),
  actions: {
    async getPhishingSimulations(): Promise<void> {
      this.phishingSettings.simulationsLoading = true;
      try {
        const response = await axiosInstance.request(api.phishingSimulations());
        const {
          data: { scheduledSimulations, simulationsHistory },
        } = response;

        this.phishingSettings = {
          ...this.phishingSettings,
          scheduledSimulations: sortSimulations(scheduledSimulations, -1),
          simulationsHistory: sortSimulations(simulationsHistory),
        };
      } catch (error) {
        console.error(error);
      }

      this.phishingSettings.simulationsLoading = false;
    },
    async getSecurityTrainingSettings(): Promise<void> {
      this.showSkeletonLoader = true;

      try {
        const response = await axiosInstance.request(api.securityTrainingSettings());

        const {
          trainingEnabled,
          enableInductionEnrolment,
          trainingCourses,
          phishingSimulationEnabled,
          phishingAutoEnrolment,
          adaptiveTrainingEnabled,
        } = response.data;

        this.securityTrainingSettings = {
          trainingEnabled,
          enableInductionEnrolment,
          adaptiveTrainingEnabled,
          trainingCourses: Object.entries(trainingCourses || {})
            .filter(([, value]) => value)
            .map(([key]) => key) as ComplianceTraining[],
        };

        this.phishingSettings = {
          ...this.phishingSettings,
          phishingSimulationEnabled,
          phishingAutoEnrolment,
        };
      } catch (error) {
        console.error(error);
      }

      this.showSkeletonLoader = false;
    },
    async getSecurityAwarenessSettings(): Promise<void> {
      this.showSkeletonLoader = true;

      try {
        const response = await axiosInstance.request(api.securityTrainingSettings());

        this.satConnections = normalizeSatSettingsResponse(
          response.data.emailProviderConfigurations || {}
        );
        this.preferredLanguageCode = response.data.preferredLanguageCode ?? null;
        this.randomJourney = response.data.randomJourney ?? false;
        this.tenancyId = response.data.tenancyId;
      } catch (error) {
        console.error(error);
      }

      this.showSkeletonLoader = false;
    },
    async deleteSatConfig(provider: SatServiceProvider) {
      this.loading = true;

      const request = {
        ...api.deleteSatConnection(provider),
      };

      try {
        const response = await axiosInstance.request(request);

        this.satConnections = normalizeSatSettingsResponse(
          response.data.emailProviderConfigurations || {}
        );
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async createSatConfig() {
      this.loading = true;

      const request = {
        ...api.securityTrainingSettings(),
        method: "POST",
        data: {},
      };

      try {
        await axiosInstance.request(request);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async updateSatConfig(
      data: Partial<SatEmailProviderConfig>,
      service: SatServiceProvider,
      preferredLanguageCode?: SatLocale
    ) {
      this.loading = true;

      const request = {
        ...api.securityTrainingSettings(),
        method: "put",
        data: {
          ...(preferredLanguageCode ? { preferredLanguageCode } : {}),
          emailProviderConfigurations: {
            [service]: data,
          },
        },
      };

      try {
        const response = await axiosInstance.request(request);

        const { emailProviderConfigurations = {}, preferredLanguageCode } = response.data;

        this.satConnections = normalizeSatSettingsResponse(emailProviderConfigurations);
        this.preferredLanguageCode = preferredLanguageCode || null;
        this.tenancyId = response.data.tenancyId;
        this.randomJourney = response.data.randomJourney ?? false;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async activateMultipleSatConfigs(
      services: SatServiceProvider[],
      preferredLanguageCode?: SatLocale
    ) {
      const request = {
        ...api.securityTrainingSettings(),
        method: "put",
        data: {
          ...(preferredLanguageCode ? { preferredLanguageCode } : {}),
          emailProviderConfigurations: {
            ...services.reduce(
              (acc, curr) => {
                acc[curr] = {
                  status: SatConfigurationStatus.ACTIVATED,
                };

                return acc;
              },
              {} as Record<Partial<SatServiceProvider>, Partial<SatEmailProviderConfig>>
            ),
          },
        },
      };

      try {
        const response = await axiosInstance.request(request);

        this.satConnections = normalizeSatSettingsResponse(
          response.data.emailProviderConfigurations || ({} as SatEmailProvidersConfiguration)
        );

        this.preferredLanguageCode = response.data.preferredLanguageCode || null;
        this.tenancyId = response.data.tenancyId;
        this.randomJourney = response.data.randomJourney ?? false;
      } catch (error) {
        console.error(error);
      }
    },
    async searchGroups({
      searchTerm,
      service,
    }: {
      searchTerm: string;
      service: ServiceSelection;
    }): Promise<Group[]> {
      const request = {
        ...api.groups(searchTerm, { page: 0, pageSize: 10 }),
        params: {
          name: searchTerm,
          page: 0,
          pageSize: 10,
          cloudServices: [service],
        },
      };
      const { data } = await axiosInstance.request(request);
      return data.items.map((v: { groupId: string; name: string; service: Service }) => {
        return {
          ...v,
          serviceName: v.service,
          groupName: v.name,
        };
      });
    },
    async updatePhishingSettings({
      phishingSimulationEnabled,
      phishingAutoEnrolment,
    }: PhishingSimulationSettings): Promise<void> {
      this.loading = true;

      const request = {
        ...api.securityTrainingSettings(),
        method: "put",
        data: {
          phishingAutoEnrolment,
          phishingSimulationEnabled,
        },
      };

      try {
        const response = await axiosInstance.request(request);

        const { phishingSimulationEnabled, phishingAutoEnrolment } = response.data;

        this.phishingSettings = {
          ...this.phishingSettings,
          phishingSimulationEnabled,
          phishingAutoEnrolment,
        };
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async getEmailTemplate(emailId: string): Promise<{ data: SecurityTrainingEmailData } | null> {
      const request = {
        ...api.securityAwarenessEmailTemplate(),
        params: {
          emailId,
        },
      };

      try {
        return axiosInstance.request(request);
      } catch (error) {
        console.error(error);

        return Promise.reject();
      }
    },
    async updateTrainingSettings(newSettings: SecurityTrainingSettings): Promise<void> {
      const { trainingCourses, ...rest } = newSettings;
      this.loading = true;
      const request = {
        ...api.securityTrainingSettings(),
        method: "put",
        data: {
          ...rest,
          trainingCourses: (Object.keys(ComplianceTraining) as ComplianceTraining[]).reduce(
            (acc, curr) => {
              acc[curr] = trainingCourses.includes(curr);
              return acc;
            },
            {} as Record<ComplianceTraining, boolean>
          ),
        },
      };

      try {
        const response = await axiosInstance.request(request);
        const {
          trainingEnabled,
          enableInductionEnrolment,
          trainingCourses,
          adaptiveTrainingEnabled,
        } = response.data;

        this.securityTrainingSettings = {
          trainingEnabled,
          enableInductionEnrolment,
          trainingCourses,
          adaptiveTrainingEnabled,
        };
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async updateSettings(partialSettings: DeepPartial<SatSettings>): Promise<void> {
      this.loading = true;
      const request = {
        ...api.securityTrainingSettings(),
        method: "put",
        data: {
          ...partialSettings,
        },
      };

      try {
        const response = await axiosInstance.request(request);

        this.preferredLanguageCode = response.data.preferredLanguageCode || null;
        this.satConnections = normalizeSatSettingsResponse(
          response.data.emailProviderConfigurations || ({} as SatEmailProvidersConfiguration)
        );
        this.tenancyId = response.data.tenancyId;
        this.randomJourney = response.data.randomJourney ?? false;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async sendTestEmails(service: SatServiceProvider) {
      const request = {
        ...api.sendSecurityAwarenessTestEmails(),
        data: {
          emailProviderType: service,
        },
      };
      try {
        await axiosInstance.request(request);
      } catch (error) {
        console.error(error);
      }
    },
    async confirmEmails(service: Service) {
      const request = {
        ...api.confirmSecurityAwarenessEmails(),
        data: {
          service,
        },
      };
      try {
        await axiosInstance.request(request);
      } catch (error) {
        console.error(error);
      }
    },
    async getMetadata() {
      const request = {
        ...api.securityAwarenessMetadata(),
      };
      this.metadataLoading = true;
      try {
        const response = await axiosInstance.request(request);
        this.metadata = response.data;
      } catch (error) {
        console.error("Error fetching security awareness metadata:", error);
      } finally {
        this.metadataLoading = false;
      }
    },
    async dismissSaBanner() {
      try {
        const request = await axiosInstance.request({
          ...api.dismissSaBanner(),
        });
        await axiosInstance.request(request);
        await this.getMetadata();
      } catch (error) {
        console.error("Error updating banner dismissed state:", error);
      }
    },
    async getCoursePreviewRecipients() {
      try {
        const response = await axiosInstance.request({
          ...api.coursePreviewRecipients(),
        });

        this.coursePreviewRecipients = response.data;
      } catch (error) {
        console.error("Error updating banner dismissed state:", error);
      }
    },
    async sendCoursePreview(data: { course: CoursePreview; recipient: string }) {
      try {
        await axiosInstance.request({
          ...api.coursePreview(),
          data,
        });

        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.securityAwarenessTraining.coursePreviewSent", {
            course: i18n.global.t(`securityAwarenessSettings.coursePreview.${data.course}`),
            recipient: data.recipient,
          })
        );
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          throw error.response?.data;
        }
        throw error;
      }
    },
    async sendTraining(data: SendSecurityAwarenessTrainingPayload): Promise<void> {
      try {
        await axiosInstance.request({
          ...api.sendSecurityAwarenessTraining(),
          data,
        });

        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.securityAwarenessTraining.courseResentAfterSync", {
            email: data.userEmail,
          })
        );
      } catch (error) {
        console.error(error);
      }
    },
    async resetJourney(updateData = true) {
      if (updateData) {
        this.loading = true;
      }

      try {
        await axiosInstance.request({
          ...api.resetJourney(),
        });

        if (updateData) {
          await this.getPhishingSimulations();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async getAllowlistAppLink(): Promise<string | null> {
      try {
        const { data } = await axiosInstance.request<{ url: string }>(api.allowlistAppLink());
        return data.url;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
  },
});

// Sort simulations in descending order in case of historic simulations and in ascending order in case of scheduled
export function sortSimulations(
  simulations: SecurityTrainingData[] = [],
  order: number = 1
): SecurityTrainingData[] {
  return simulations.slice().sort((a, b) => {
    const startDateDiff = b.startDate - a.startDate;

    if (startDateDiff === 0) {
      return (b.endDate - a.endDate) * order;
    }

    return startDateDiff * order;
  });
}
